import React, { memo, useState } from "react";
import {
  Edit3,
  Trash2,
  ShieldCheck,
  AlertTriangle,
  Layers,
  Check,
  X,
  User,
  Ruler,
  Gem,
  PenTool,
  Siren,
  MessageSquare,
  Send,
  ExternalLink,
  Disc,
} from "lucide-react";
import DaysBadge from "./DaysBadge";
import TransitToggle from "./TransitToggle";
import EngravingDisplay from "./EngravingDisplay";
import {
  PRODUCTION_STATUS_CONFIG,
  KANBAN_ORDER,
  isStatusAguardandoInjecao,
  isStatusFaseCeraMolde,
} from "../../config/productionStatuses";
import { productionService } from "../../services/productionService";
import { useCommunication } from "../../contexts/CommunicationContext";
import { useCatalogContext } from "../../contexts/CatalogContext";
import { cleanSku, getBaseSku } from "../../utils/skuUtils";

// Helper de Data
const getIsoDate = (val) => {
  if (!val) return "";
  if (val.toDate) return val.toDate().toISOString().split("T")[0];
  if (typeof val === "string") {
    return val.split("T")[0].split(" ")[0];
  }
  return "";
};

// Helper PE Status Labels
const PE_STATUS_LABELS = {
  pe_solicitado: "SOLICITADO",
  pe_imprimindo: "IMPRIMINDO",
  pe_cura: "CURA",
  pe_fundicao: "FUNDIÇÃO",
  pe_fundidos: "FUNDIDOS",
  pe_pre_escapulario: "PRÉ-ESCAPULÁRIO",
  pe_acabamento: "ACABAMENTO EXTERNO",
  pe_cravacao: "CRAVAÇÃO/GRAVAÇÃO",
  pe_polimento: "POLIMENTO",
  pe_revisao: "REVISÃO",
  pe_finalizacao: "RESINA/FINALIZAÇÃO",
  pe_conferencia: "CONFERÊNCIA",
  pe_interceptado: "INTERCEPTADO",
};

const getPEStatusLabel = (statusId) => {
  if (!statusId) return "";
  if (PE_STATUS_LABELS[statusId]) {
    return PE_STATUS_LABELS[statusId];
  }
  try {
    const saved = localStorage.getItem("pe_custom_statuses");
    if (saved) {
      const parsed = JSON.parse(saved);
      const found = parsed.find(s => s.id === statusId);
      if (found) return found.label.toUpperCase();
    }
  } catch (e) {}
  return statusId.replace(/^pe_/, "").replace(/_/g, " ").toUpperCase();
};

// Helper Anti-ND
const hasValue = (val) => {
  if (!val) return false;
  const s = String(val).trim().toUpperCase();
  return s !== "" && s !== "ND" && s !== "-" && s !== "N/A";
};

// Componente memoizado linha de produção
const ProductionItemRow = memo(({
  user,
  order,
  isSelected,
  toggleSelect,
  handleMoveStatus,
  onToggleTransit,
  handleDeleteOrder,
  onTogglePE,
  setEditingOrder,
  onUpdateDate,
  findCatalogItem
}) => {
  const { openQuickOrder, openQuickProduct } = useCommunication();
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [tempDate, setTempDate] = useState("");
  const [isMuralOpen, setIsMuralOpen] = useState(false);
  const [newMessageText, setNewMessageText] = useState("");
  const [isSending, setIsSending] = useState(false);

  const messages = order.full_data?.messages || order.messages || [];
  const totalMessages = messages.length;
  const hasUserComments = messages.some(m => m.type === "user");

  const handleSendMessage = async () => {
    const text = newMessageText.trim();
    if (!text || isSending) return;

    setIsSending(true);
    try {
      const authorName = user?.name || "Usuário";
      await productionService.addMuralMessage(order.id, text, "user", authorName);
      setNewMessageText("");
    } catch (e) {
      console.error("Erro ao enviar mensagem para o mural", e);
      alert("Erro ao enviar mensagem: " + e.message);
    } finally {
      setIsSending(false);
    }
  };

  const startEditing = (e) => {
    e.stopPropagation();
    setIsEditingDate(true);
    setTempDate(getIsoDate(order.customCreatedAt || order.createdAt));
  };

  const cancelEditing = (e) => {
    e.stopPropagation();
    setIsEditingDate(false);
    setTempDate("");
  };

  const saveDate = async (e) => {
    e.stopPropagation();
    if (onUpdateDate) await onUpdateDate(order.id, tempDate);
    setIsEditingDate(false);
  };

  const { isMoldeSku } = useCatalogContext() || {};
  const catalog = findCatalogItem ? findCatalogItem(order.sku) : null;
  const cleanPai = getBaseSku(cleanSku(order.sku));

  const isBorracha = Boolean(
    order.tem_molde ||
    order.full_data?.tem_molde ||
    order.specs?.tem_molde ||
    catalog?.tem_molde ||
    catalog?.full_data?.tem_molde ||
    catalog?.full_data?.processo_fabricacao === "borracha" ||
    (isMoldeSku && (isMoldeSku(order.sku) || isMoldeSku(cleanPai)))
  );

  // A tag BORRACHA só é sinalizada durante as etapas de cera/molde (pré-injeção e injetado aguardando árvore).
  // Uma vez em Fundição, Banca ou além, segue o fluxo normal da oficina em metal.
  const hasBorracha = isBorracha && isStatusFaseCeraMolde(order.status);

  const isNatural = String(order?.specs?.stoneType || "")
    .toLowerCase()
    .includes("natural");
  const isDivergent =
    hasValue(order.specs?.standardColor) &&
    hasValue(order.specs?.stoneColor) &&
    order.specs.standardColor !== "MANUAL" &&
    order.specs.standardColor.toUpperCase() !==
    order.specs.stoneColor.toUpperCase();

  const showSize = hasValue(order.specs?.size);
  const showColor = hasValue(order.specs?.stoneColor);
  const showMainSpecs = showSize || showColor;

  return (
    <div
      className={`
        relative group transition-all hover:bg-slate-50
        ${isSelected ? "bg-purple-50/70" : "bg-white"}
        ${hasValue(order.exception) ? "border-l-4 border-l-red-500" : ""} 
      `}
    >
      <div className="p-4 flex flex-col gap-3 lg:flex-row lg:p-5 lg:gap-6 lg:items-center w-full">
        
        {/* =========================================
            ZONA 1 & 2: DADOS DO ITEM (Esquerda/Topo)
           ========================================= */}
        <div className="flex-1 flex flex-col w-full">
          
          {/* ZONA 1: Cabeçalho Crítico */}
          <div className="flex justify-between items-start gap-4">
            {/* Lado Esquerdo: Checkbox + SKU + Produto */}
            <div className="flex gap-3">
              <div className="mt-1 shrink-0">
                <input
                  type="checkbox"
                  className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                  checked={isSelected}
                  onChange={() => toggleSelect(order.id)}
                />
              </div>
              <div className="flex flex-col gap-0.5">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openQuickProduct(order.sku);
                    }}
                    className="text-lg font-semibold text-slate-800 hover:text-indigo-700 hover:underline tracking-tight flex items-center gap-1.5 cursor-pointer text-left group/prod transition"
                    title="Abrir Ficha Rápida do Produto (Variações & Estoque)"
                  >
                    <span>{order.sku}</span>
                    <ExternalLink size={13} className="text-indigo-400 opacity-0 group-hover/prod:opacity-100 transition shrink-0" />
                  </button>
                  
                  {/* EXCEÇÃO */}
                  {hasValue(order.exception) && (
                    <div className="bg-red-600 text-white text-[10px] font-black px-2 py-0.5 rounded shadow-md flex items-center gap-1 animate-pulse" title={order.exception}>
                      <Siren size={12} className="text-white" />
                      <span className="uppercase tracking-wider max-w-[150px] truncate">
                        {order.exception}
                      </span>
                    </div>
                  )}

                  {/* BADGE PE INTERATIVA / ESTOQUE / IMPRESSO */}
                  {order.isInterceptedPE && (
                    <button
                      onClick={(e) => { e.stopPropagation(); if (onTogglePE) onTogglePE(order); }}
                      className="group bg-purple-100 text-purple-700 text-[10px] font-bold px-2 py-0.5 rounded border border-purple-200 hover:bg-red-100 hover:text-red-700 transition-all flex items-center gap-1 cursor-pointer shadow-sm"
                      title="Remover urgência PE"
                    >
                      ⚡ INTERCEPTADO {order.interceptedFromStatus ? `(${getPEStatusLabel(order.interceptedFromStatus)})` : ""} <span className="w-0 overflow-hidden group-hover:w-auto transition-all">✖</span>
                    </button>
                  )}
                  {hasBorracha && (
                    <span 
                      className="bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-black px-2 py-0.5 rounded shadow-sm flex items-center gap-1 uppercase tracking-wider cursor-help transition-colors select-none"
                      title="Item com Molde de Borracha (Injeção Direta — sem etapa de impressão 3D)"
                    >
                      <Disc size={11} className="text-amber-100 shrink-0 animate-spin-slow" />
                      <span>BORRACHA</span>
                    </span>
                  )}
                  {order.printed && (
                    <span className="bg-yellow-100 text-yellow-800 text-[10px] font-bold px-1.5 py-0.5 rounded border border-yellow-200 flex items-center justify-center cursor-help" title="Impresso">I</span>
                  )}
                  {order.fromStock && !order.isPE && !order.isInterceptedPE && (
                    <span className="bg-emerald-600 text-white text-[9px] px-1.5 rounded font-bold shadow-sm">ESTOQUE</span>
                  )}
                  {order.isPE && !order.isInterceptedPE && (
                    <span className="bg-purple-600 text-white text-[9px] px-1.5 rounded font-bold flex items-center gap-1 shadow-sm"><Layers size={10} /> PE</span>
                  )}
                </div>
                <p className="text-sm text-slate-600 line-clamp-1 font-medium title={catalog?.name}">
                  {catalog?.name || "Carregando..."}
                </p>
              </div>
            </div>

            {/* Lado Direito: Badge de Data (Idade) */}
            <div className="text-right shrink-0">
              {isEditingDate ? (
                <div className="flex flex-col gap-1 items-end bg-white p-2 rounded-lg border shadow-lg absolute z-20 right-4 animate-in fade-in zoom-in duration-200">
                  <input
                    type="date"
                    className="text-xs p-1 border rounded font-mono bg-slate-50 outline-none focus:border-purple-500"
                    value={tempDate}
                    onChange={(e) => setTempDate(e.target.value)}
                  />
                  <div className="flex gap-2 w-full justify-end mt-1">
                    <button onClick={saveDate} className="bg-emerald-100 text-emerald-700 p-1 rounded hover:bg-emerald-200 transition-colors"><Check size={14} /></button>
                    <button onClick={cancelEditing} className="bg-red-100 text-red-700 p-1 rounded hover:bg-red-200 transition-colors"><X size={14} /></button>
                  </div>
                </div>
              ) : (
                <div className="group/date relative cursor-pointer" onClick={startEditing}>
                  <DaysBadge date={order.customCreatedAt || order.createdAt} order={order} />
                  <div className="opacity-0 group-hover/date:opacity-100 transition-opacity absolute -top-2 -right-2 bg-white rounded-full shadow-sm border border-slate-200 p-1 text-blue-500">
                    <Edit3 size={10} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ZONA 2: Agrupamento de Atributos (Badges) */}
          {(showMainSpecs || hasValue(order.specs?.stoneBatch) || hasValue(order.specs?.finishing) || isNatural || hasValue(order.specs?.engraving)) && (
            <div className="flex flex-wrap gap-2 pt-3 mt-3 border-t border-slate-100 items-center">
              {showSize && (
                <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded border border-slate-200 shadow-sm" title="Tamanho do Aro">
                  <Ruler size={12} className="text-slate-400" />
                  <span className="font-bold text-xs text-slate-700">{order.specs.size}</span>
                </div>
              )}
              {showColor && (
                <div className={`flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded border border-slate-200 shadow-sm font-bold text-xs ${isDivergent ? "text-amber-600" : "text-slate-700"}`} title="Cor da Pedra">
                  <Gem size={12} className={isDivergent ? "text-amber-500" : "text-purple-400"} />
                  <span className="uppercase tracking-tight">{order.specs.stoneColor}</span>
                  {isDivergent && <AlertTriangle size={12} className="text-amber-500 animate-pulse" />}
                </div>
              )}
              {hasValue(order.specs?.stoneBatch) && (
                <span className="bg-blue-50 text-blue-700 px-1.5 py-1 rounded text-[10px] font-mono border border-blue-100 shadow-sm">
                  <span className="opacity-50">#</span>{order.specs.stoneBatch}
                </span>
              )}
              {hasValue(order.specs?.finishing) && (
                <span className="bg-slate-100 text-slate-600 px-1.5 py-1 rounded text-[10px] border border-slate-200 shadow-sm uppercase font-bold">
                  {order.specs.finishing}
                </span>
              )}
              {isNatural && (
                <span className="bg-indigo-50 text-indigo-600 px-1.5 py-1 rounded text-[10px] font-bold border border-indigo-100 shadow-sm flex items-center gap-1">
                  <ShieldCheck size={10} /> Natural
                </span>
              )}
              {hasValue(order.specs?.engraving) && (
                <div className="ml-auto">
                  <EngravingDisplay rawText={order.specs.engraving} mode="compact" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* =========================================
            ZONA 3: RODAPÉ DO ITEM (Direita/Baixo)
           ========================================= */}
        <div className="w-full lg:w-72 xl:w-96 shrink-0 lg:border-l lg:border-t-0 border-t border-slate-100 lg:pl-6 pt-3 lg:pt-0 mt-2 lg:mt-0 flex flex-col gap-3">
          
          {/* Cliente e Número do Pedido Raiz */}
          <div className="flex items-center gap-2 text-sm text-slate-500 truncate w-full">
            <User size={14} className="shrink-0 text-slate-400" />
            <span className="font-bold text-slate-700 truncate" title={order.order?.customer?.name}>
              {order.order?.customer?.name || order.customerName || "Balcão"}
            </span>
            {hasValue(order.orderNumber || order.order?.number) && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openQuickOrder(order.orderNumber || order.order?.number);
                }}
                className="font-bold text-sm md:text-base tracking-tight text-slate-700 hover:text-indigo-700 bg-slate-100 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 px-2 py-0.5 rounded shadow-xs ml-auto shrink-0 flex items-center gap-1 transition cursor-pointer group/ped"
                title="Abrir Ficha Rápida do Pedido"
              >
                <span className="text-[10px] text-slate-400 group-hover/ped:text-indigo-500 font-bold uppercase tracking-widest mt-0.5">PED</span>
                <span>#{order.orderNumber || order.order?.number}</span>
                <ExternalLink size={11} className="text-indigo-400 opacity-0 group-hover/ped:opacity-100 transition shrink-0" />
              </button>
            )}
          </div>

          {/* Segunda linha do Rodapé: Status + Botões */}
          <div className="flex justify-between items-center gap-2">
            
            {/* Box do Status */}
            <div className="flex-1 min-w-0">
              <select
                className={`
                  w-full text-[10px] font-bold uppercase p-2 rounded border cursor-pointer outline-none focus:ring-2 focus:ring-purple-200 transition-all appearance-none text-center shadow-sm select-none
                  ${order.status === "CANCELADO"
                    ? "bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-800"
                  }
                `}
                value={order.status}
                onChange={(e) => handleMoveStatus(order.id, e.target.value)}
                onClick={(e) => e.stopPropagation()}
              >
                {KANBAN_ORDER.map((s) => (
                  <option key={s} value={s}>{PRODUCTION_STATUS_CONFIG[s]?.label || s}</option>
                ))}
                {!KANBAN_ORDER.includes(order.status) && (
                  <option value={order.status}>⚠ {order.status}</option>
                )}
              </select>
            </div>

            {/* Ícones de Ação */}
            <div className="flex justify-end gap-1 items-center shrink-0">
              <TransitToggle order={order} onToggle={onToggleTransit} />

              <button
                onClick={(e) => { e.stopPropagation(); setIsMuralOpen(!isMuralOpen); }}
                className={`p-1.5 rounded transition-all flex items-center gap-1 ${
                  isMuralOpen 
                    ? "text-purple-600 bg-purple-50 hover:bg-purple-100" 
                    : hasUserComments 
                      ? "text-purple-600 bg-purple-50/50 hover:bg-purple-50"
                      : "text-slate-400 hover:text-purple-600 hover:bg-purple-50"
                }`}
                title="Abrir Mural de Mensagens"
              >
                <MessageSquare size={16} />
                {totalMessages > 0 && (
                  <span className="text-[10px] font-bold px-1 rounded-full bg-purple-100 text-purple-700 min-w-[16px] text-center">
                    {totalMessages}
                  </span>
                )}
              </button>
              
              <button
                onClick={(e) => { e.stopPropagation(); setEditingOrder(order); }}
                className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                title="Editar/Converter Item"
              >
                <Edit3 size={16} />
              </button>
              
              {order.status === "CANCELADO" && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteOrder(order); }}
                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                  title="Excluir Definitivamente"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>

          </div>
        </div>

      </div>

      {/* SEÇÃO DO MURAL */}
      {isMuralOpen && (
        <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-3 lg:px-5 lg:py-4 flex flex-col gap-3 animate-in slide-in-from-top duration-200">
          <div className="flex justify-between items-center">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <MessageSquare size={14} className="text-purple-500" /> Mural de Mensagens &amp; Histórico
            </h4>
            <span className="text-[10px] text-slate-400 font-medium">
              {totalMessages} registro{totalMessages !== 1 ? "s" : ""}
            </span>
          </div>

          {/* LISTA DE MENSAGENS */}
          <div 
            className="flex flex-col gap-2 max-h-60 overflow-y-auto pr-1 custom-scrollbar scroll-smooth"
            ref={(el) => {
              if (el) {
                el.scrollTop = el.scrollHeight;
              }
            }}
          >
            {messages.length === 0 ? (
              <div className="text-center py-6 text-slate-400 text-xs italic">
                Nenhuma mensagem ou log registrado para este pedido.
              </div>
            ) : (
              messages.map((msg) => {
                const isSystem = msg.type === "system";
                const dateStr = msg.timestamp ? new Date(msg.timestamp).toLocaleString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit"
                }) : "";

                if (isSystem) {
                  return (
                    <div key={msg.id} className="flex justify-center my-0.5">
                      <div className="bg-slate-100 border border-slate-200 text-slate-600 rounded px-2.5 py-1 text-[11px] max-w-full text-center flex items-center gap-1.5 leading-relaxed font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                        <span>{msg.text}</span>
                        <span className="text-[9px] text-slate-400 font-mono ml-1">{dateStr}</span>
                      </div>
                    </div>
                  );
                }

                // User comment bubble
                const isCurrentUser = user && msg.author && (msg.author.toLowerCase() === user.name?.toLowerCase());
                return (
                  <div key={msg.id} className={`flex flex-col gap-0.5 max-w-[85%] ${isCurrentUser ? "self-end items-end" : "self-start items-start"}`}>
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500 px-1">
                      <span>{msg.author || "Usuário"}</span>
                      <span className="text-[8px] font-normal text-slate-400 font-mono">{dateStr}</span>
                    </div>
                    <div className={`rounded-xl px-3 py-2 text-xs shadow-sm leading-relaxed ${
                      isCurrentUser 
                        ? "bg-purple-600 text-white rounded-tr-none" 
                        : "bg-white border border-slate-200 text-slate-700 rounded-tl-none"
                    }`}>
                      {msg.text}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* INPUT PARA ENVIAR MENSAGEM */}
          <div className="flex gap-2 items-center mt-1 border-t border-slate-200 pt-2.5">
            <input
              type="text"
              placeholder="Escreva uma observação..."
              className="flex-1 bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-purple-500 shadow-inner text-slate-800"
              value={newMessageText}
              onChange={(e) => setNewMessageText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={isSending}
            />
            <button
              onClick={handleSendMessage}
              disabled={isSending || !newMessageText.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg p-1.5 transition-all shadow-sm hover:shadow disabled:bg-purple-400 disabled:cursor-not-allowed cursor-pointer shrink-0 flex items-center justify-center"
              title="Enviar observação"
            >
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  
  const pItem = prevProps.order;
  const nItem = nextProps.order;
  
  // React.memo Deep Compare for `order` updates
  if (pItem.id !== nItem.id) return false;
  
  if (pItem.status !== nItem.status) return false;
  if (pItem.updated !== nItem.updated) return false; // Covers pocketbase pushes immediately
  
  if (pItem.exception !== nItem.exception) return false;
  if (pItem.transit_status !== nItem.transit_status) return false;
  if (pItem.isInterceptedPE !== nItem.isInterceptedPE) return false;
  if (pItem.printed !== nItem.printed) return false;

  // Compare messages array length to trigger re-render on changes
  const pMessagesLength = Array.isArray(pItem.full_data?.messages) ? pItem.full_data.messages.length : 0;
  const nMessagesLength = Array.isArray(nItem.full_data?.messages) ? nItem.full_data.messages.length : 0;
  if (pMessagesLength !== nMessagesLength) return false;

  // React to catalog load completion (race condition fix)
  const prevCat = prevProps.findCatalogItem ? prevProps.findCatalogItem(pItem.sku) : null;
  const nextCat = nextProps.findCatalogItem ? nextProps.findCatalogItem(nItem.sku) : null;
  if (prevCat?.name !== nextCat?.name) return false;

  return true; // No relevant prop changed
});

export default ProductionItemRow;
